import { findClosestMatchingLocale } from '@shared/hooks/useGeoLocale';

type LocaleParams = Readonly<{
  language: string | undefined;
  culture: string | undefined;
  currency: string | undefined;
}>;

type ExplicitLocaleChoice = Readonly<{
  hasExplicitLanguageChoice: boolean;
  hasExplicitCurrencyChoice: boolean;
}>;

/**
 * Which URL locale params are a real choice, parsed the way `useGeoBasedLocale` parses them. A bare
 * `?lang=en` only lands on the first `en` row (en-US / USD) by table order, and unrecognised values
 * (`?l=pt`, `?currency=XYZ`) resolve to nothing, so neither should stop the venue from deciding.
 */
export const getExplicitLocaleChoice = ({ language, culture, currency }: LocaleParams): ExplicitLocaleChoice => {
  // Same precedence as findClosestMatchingLocale: the culture param beats a language's region suffix.
  const region = culture ?? language?.split('-')[1];
  const hasExplicitLanguageChoice = !!region && !!findClosestMatchingLocale({ culture: region });
  const hasExplicitCurrency = !!currency && !!findClosestMatchingLocale({ currency });

  return {
    hasExplicitLanguageChoice,
    // A recognised region drives currency in useGeoBasedLocale too.
    hasExplicitCurrencyChoice: hasExplicitLanguageChoice || hasExplicitCurrency,
  };
};
